import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

@Component({
    selector: 'circle-progress',
    template: `
    <svg [attr.width]="svgSize" [attr.height]="svgSize" [attr.viewBox]="'0 0 ' + svgSize + ' ' + svgSize">
      <!-- Inner (background) circle -->
      <circle
        [attr.cx]="center"
        [attr.cy]="center"
        [attr.r]="normalizedRadius"
        [attr.stroke]="innerStrokeColor"
        [attr.stroke-width]="innerStrokeWidth"
        fill="none"
      />
      <!-- Outer (progress) circle -->
      <circle
        [attr.cx]="center"
        [attr.cy]="center"
        [attr.r]="normalizedRadius"
        [attr.stroke]="outerStrokeColor"
        [attr.stroke-width]="outerStrokeWidth"
        [attr.stroke-dasharray]="circumference"
        [attr.stroke-dashoffset]="strokeDashoffset"
        stroke-linecap="round"
        fill="none"
        [style.transition]="animation ? 'stroke-dashoffset ' + animationDuration + 'ms ease-in-out' : 'none'"
        transform="rotate(-90)"
        [attr.transform-origin]="center + ' ' + center"
      />
      <!-- Title text -->
      <text
        *ngIf="showTitle"
        [attr.x]="center"
        [attr.y]="showSubtitle ? center - 2 : center"
        text-anchor="middle"
        dominant-baseline="central"
        [attr.fill]="titleColor"
        [attr.font-size]="titleFontSize"
      >{{ titleDisplay }}{{ showUnits ? units : '' }}</text>
      <!-- Subtitle text -->
      <text
        *ngIf="showSubtitle && subtitle"
        [attr.x]="center"
        [attr.y]="center + 10"
        text-anchor="middle"
        dominant-baseline="central"
        [attr.fill]="subtitleColor"
        [attr.font-size]="subtitleFontSize"
      >{{ subtitle }}</text>
    </svg>
  `,
    styles: [`
    :host {
      display: inline-block;
      line-height: 0;
    }
  `],
    standalone: false
})
export class CircularProgressComponent implements OnChanges {
  @Input() percent: number = 0;
  @Input() radius: number = 60;
  @Input() outerStrokeWidth: number = 3;
  @Input() innerStrokeWidth: number = 3;
  @Input() outerStrokeColor: string = '#78C000';
  @Input() innerStrokeColor: string = 'rgba(0, 0, 0, 0)';
  @Input() titleColor: string = '#000000';
  @Input() titleFontSize: number = 10;
  @Input() subtitleColor: string = '#666666';
  @Input() subtitleFontSize: number = 8;
  @Input() animation: boolean = false;
  @Input() animationDuration: number = 500;
  @Input() showTitle: boolean = true;
  @Input() showSubtitle: boolean = false;
  @Input() showUnits: boolean = true;
  @Input() title: string | number;
  @Input() subtitle: string;
  @Input() units: string = '%';
  @Input() backgroundColor: string = '#ffffff';
  @Input() backgroundOpacity: number = 0;
  @Input() startFromZero: boolean = false;
  @Input() tooltip: string;
  @Input() container: string;

  svgSize: number;
  center: number;
  normalizedRadius: number;
  circumference: number;
  strokeDashoffset: number;
  titleDisplay: string | number;

  ngOnChanges(_changes: SimpleChanges): void {
    this.calculate();
  }

  private calculate(): void {
    const maxStroke = Math.max(this.outerStrokeWidth, this.innerStrokeWidth);
    this.svgSize = (this.radius + maxStroke) * 2;
    this.center = this.svgSize / 2;
    this.normalizedRadius = this.radius;
    this.circumference = 2 * Math.PI * this.normalizedRadius;

    const clampedPercent = Math.max(0, Math.min(100, this.percent || 0));
    this.strokeDashoffset = this.circumference - (clampedPercent / 100) * this.circumference;

    this.titleDisplay = this.title !== undefined && this.title !== null ? this.title : (this.percent || 0);
  }
}
